import {
    ChangeDetectionStrategy,
    Component,
    CUSTOM_ELEMENTS_SCHEMA,
    ElementRef,
    OnDestroy,
    OnInit,
    inject,
    input,
    output
} from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';
import { Button, InputText, TextValueAccessor } from '@shui/angular';
import { LinkMenuState, LinkMenuSubmission } from '../../models/sh-editor-overlay.model';
import { normalizeEditorLinkHref } from '../../utils/sh-editor-link.util';

@Component({
    selector: 'sh-editor-link-menu',
    templateUrl: './sh-editor-link-menu.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [ReactiveFormsModule, TranslatePipe, InputText, TextValueAccessor, Button],
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
    host: {
        class: 'fixed z-[1100] flex w-[min(340px,calc(100vw-32px))] flex-col gap-3 rounded-xl border border-[var(--sh-editor-divider)] bg-[var(--sh-editor-toolbar-bg)] p-3 text-[var(--sh-editor-text)] shadow-[0_16px_32px_rgba(0,0,0,0.32)]',
        'data-sh-editor-link-menu': 'true',
        'data-testid': 'global-editor-menu-link',
        '[style.top.px]': 'menu().top',
        '[style.left.px]': 'menu().left',
        '(mousedown)': 'handleMouseDown($event)'
    }
})
export class ShEditorLinkMenuComponent implements OnInit, OnDestroy {
    public readonly menu = input.required<LinkMenuState>();

    public readonly closed = output<void>();
    public readonly linkApplied = output<LinkMenuSubmission>();
    public readonly linkRemoved = output<string>();

    public readonly linkTextControl = new FormControl('', { nonNullable: true });
    public readonly linkUrlControl = new FormControl('', { nonNullable: true });

    private readonly hostElement = inject<ElementRef<HTMLElement>>(ElementRef);

    private focusTimer?: ReturnType<typeof setTimeout>;

    public ngOnInit(): void {
        this.initControls();
        this.scheduleLinkUrlFocus();
    }

    private initControls(): void {
        const menu = this.menu();

        this.linkTextControl.setValue(menu.selectedText, { emitEvent: false });
        this.linkUrlControl.setValue(menu.href, { emitEvent: false });
    }

    public ngOnDestroy(): void {
        clearTimeout(this.focusTimer);
    }

    protected close(event: MouseEvent): void {
        event.preventDefault();
        event.stopPropagation();
        this.closed.emit();
    }

    protected applyLink(event: MouseEvent): void {
        event.preventDefault();
        event.stopPropagation();

        const href = normalizeEditorLinkHref(this.linkUrlControl.value.trim());
        const text = this.linkTextControl.value.trim();
        if (!href || !text) {
            return;
        }

        this.linkApplied.emit({ text, href });
    }

    protected removeLink(event: MouseEvent): void {
        event.preventDefault();
        event.stopPropagation();

        const text = this.linkTextControl.value.trim() || this.menu().selectedText;
        if (!text) {
            return;
        }

        this.linkRemoved.emit(text);
    }

    protected canApplyLink(): boolean {
        const href = normalizeEditorLinkHref(this.linkUrlControl.value.trim());
        const text = this.linkTextControl.value.trim();

        return Boolean(href) && Boolean(text);
    }

    protected handleMouseDown(event: MouseEvent): void {
        event.stopPropagation();
    }

    private scheduleLinkUrlFocus(): void {
        clearTimeout(this.focusTimer);
        this.focusTimer = setTimeout(() => {
            this.focusLinkUrlInput();
        }, 0);
    }

    private focusLinkUrlInput(): void {
        const inputHost = this.hostElement.nativeElement.querySelector<HTMLElement>('[data-sh-editor-link-url="true"]');
        if (!(inputHost instanceof HTMLElement)) {
            return;
        }

        const shadowInput = inputHost.shadowRoot?.querySelector<HTMLInputElement>('input');
        if (shadowInput instanceof HTMLInputElement) {
            shadowInput.focus();
            shadowInput.select();
            return;
        }

        inputHost.focus();
    }
}
